import { ThemeProvider, ThemeProviderProps } from '@emotion/react';
import { defaultMapUiTheme } from './theme';
import { GlobalMapUIStyle } from './GlobalMapUIStyle';
import { objectValues } from '../../utils';
import { FC } from 'react';

export const getThemePropForPath = (theme, path = []) => {
  if (!path) {
    return theme;
  }
  return path.reduce(
    (themeDive, pathKey) => themeDive && themeDive[pathKey],
    theme
  );
};

export const compileThemeValue = (subTheme, theme) => {
  if (typeof subTheme !== 'function') {
    return subTheme;
  }
  let newSubTheme;
  try {
    newSubTheme = compileThemeValue(subTheme.call(theme, theme), theme);
    if (!newSubTheme) {
      throw new Error('Key not found');
    }
  } catch (err) {
    console.error(
      `Wrong input for ${subTheme}, check the key is formatted properly and it exists in the theme`,
      err
    );
    newSubTheme = subTheme;
  }
  return newSubTheme;
};

export const deepMergeWithDefaultTheme = (theme, path = []) => {
  const defaultThemeForPath = getThemePropForPath(defaultMapUiTheme, path);

  return Object.keys(defaultThemeForPath).reduce(
    (mergingTheme, subThemeKey) => {
      const defaultSubTheme = defaultThemeForPath[subThemeKey];
      const subThemePath = path.concat(subThemeKey);
      if (typeof defaultSubTheme === 'object') {
        mergingTheme[subThemeKey] = deepMergeWithDefaultTheme(
          theme,
          subThemePath
        );
      } else {
        const newSubTheme =
          getThemePropForPath(theme, subThemePath) || defaultSubTheme;
        mergingTheme[subThemeKey] = newSubTheme;
      }
      return mergingTheme;
    },
    {}
  );
};
export const compileTheme = (theme, path = []) => {
  const themeForPath = getThemePropForPath(theme, path);

  return Object.keys(themeForPath).reduce((compilingTheme, subThemeKey) => {
    const subTheme = themeForPath[subThemeKey];
    const subThemePath = path.concat(subThemeKey);
    if (typeof subTheme === 'object') {
      compilingTheme[subThemeKey] = compileTheme(theme, subThemePath);
    } else {
      const newSubTheme = getThemePropForPath(theme, subThemePath);
      compilingTheme[subThemeKey] = compileThemeValue(newSubTheme, theme);
    }
    return compilingTheme;
  }, {});
};

const addSizesForStyledSystem = (theme) => ({
  ...theme,
  sizes: objectValues(theme.screens),
});

export const prepareTheme = (theme) =>
  [deepMergeWithDefaultTheme, compileTheme, addSizesForStyledSystem].reduce(
    (acc, next) => next(acc),
    theme
  );

export const useDefaultTheme = () => prepareTheme({});

export const MapUiProvider: FC<ThemeProviderProps> = ({
  children,
  theme = {},
}) => (
  <ThemeProvider theme={prepareTheme(theme)}>
    <GlobalMapUIStyle />
    {children}
  </ThemeProvider>
);
